import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Key as KeyIcon,
  QrCodeScanner as QrCodeIcon,
  StickyNote2 as NoteIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { CREATE_VAULT_ITEM_MUTATION, VAULT_ITEMS_QUERY } from '../services/graphql';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type {
  VaultItemType,
  PasswordPayload,
  TOTPPayload,
  SecureNotePayload,
  CreditCardPayload,
  ContactPayload,
  ContactAddress,
} from '../types';
import {
  generateRandomKey,
  encryptPayload,
  encryptItemKey,
} from '../crypto/encryption';
import { normalizeTOTPSecret, isValidTOTPSecret } from '../crypto/totp';
import { COUNTRIES } from '../utils/countries';

const itemTypeOptions = [
  { type: 'password' as VaultItemType, label: 'Password', icon: <LockIcon /> },
  { type: 'totp' as VaultItemType, label: '2FA Code', icon: <KeyIcon /> },
  { type: 'secure_note' as VaultItemType, label: 'Secure Note', icon: <NoteIcon /> },
  { type: 'credit_card' as VaultItemType, label: 'Credit Card', icon: <CreditCardIcon /> },
  { type: 'contact' as VaultItemType, label: 'Contact', icon: <PersonIcon /> },
];

export default function AddItem() {
  const navigate = useNavigate();
  const { vaultKey } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const workspaceId = currentWorkspace?.id;

  const [itemType, setItemType] = useState<VaultItemType>('password');
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState('');

  // Password fields
  const [title, setTitle] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [websites, setWebsites] = useState<string[]>(['']);
  const [notes, setNotes] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // TOTP fields
  const [secret, setSecret] = useState('');

  // Secure Note fields
  const [secureNote, setSecureNote] = useState('');

  // Credit Card fields
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardPin, setCardPin] = useState('');
  const [cardPostalCode, setCardPostalCode] = useState('');
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // Contact fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<ContactAddress>({});

  const [createItem, { loading }] = useMutation(CREATE_VAULT_ITEM_MUTATION, {
    refetchQueries: workspaceId
      ? [{ query: VAULT_ITEMS_QUERY, variables: { workspaceId } }]
      : [],
    onCompleted: () => {
      navigate('/vault');
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const resetForm = () => {
    setTitle('');
    setUsername('');
    setPassword('');
    setWebsites(['']);
    setNotes('');
    setSecret('');
    setSecureNote('');
    setCardholderName('');
    setCardNumber('');
    setExpirationDate('');
    setCvv('');
    setCardPin('');
    setCardPostalCode('');
    setFullName('');
    setEmail('');
    setPhone('');
    setAddress({});
  };

  const handleTypeChange = (newType: VaultItemType) => {
    setItemType(newType);
    resetForm();
  };

  const addWebsite = () => {
    setWebsites([...websites, '']);
  };

  const removeWebsite = (index: number) => {
    if (websites.length > 1) {
      setWebsites(websites.filter((_, i) => i !== index));
    }
  };

  const updateWebsite = (index: number, value: string) => {
    const updated = [...websites];
    updated[index] = value;
    setWebsites(updated);
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      // If URL has protocol, validate directly; otherwise add https://
      new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!vaultKey) {
      setError('Vault key not available. Please log in again.');
      return;
    }

    if (!workspaceId) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      let payload: PasswordPayload | TOTPPayload | SecureNotePayload | CreditCardPayload | ContactPayload;

      if (itemType === 'password') {
        // Validate URLs
        for (const url of websites) {
          if (url && !validateUrl(url)) {
            setError(`Invalid URL: ${url}`);
            return;
          }
        }

        if (!password.trim()) {
          setError('Password is required');
          return;
        }

        payload = {
          type: 'password',
          title,
          username: username || undefined,
          password,
          websites: websites.filter((url) => url.trim()),
          notes: notes || undefined,
        };
      } else if (itemType === 'totp') {
        if (!isValidTOTPSecret(secret)) {
          setError('Please enter a valid TOTP secret (base32 format)');
          return;
        }

        payload = {
          type: 'totp',
          title,
          secret: normalizeTOTPSecret(secret),
          algorithm: 'SHA1',
          digits: 6,
          period: 30,
          notes: notes || undefined,
        };
      } else if (itemType === 'secure_note') {
        if (!secureNote.trim()) {
          setError('Note content is required');
          return;
        }

        payload = {
          type: 'secure_note',
          title,
          secureNote,
        };
      } else if (itemType === 'credit_card') {
        if (!cardNumber.trim()) {
          setError('Card number is required');
          return;
        }

        // Validate expiration date format
        if (expirationDate && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expirationDate)) {
          setError('Expiration date must be in MM/YY format');
          return;
        }

        payload = {
          type: 'credit_card',
          title,
          cardholderName: cardholderName || undefined,
          cardNumber,
          expirationDate: expirationDate || undefined,
          cvv: cvv || undefined,
          cardPin: cardPin || undefined,
          postalCode: cardPostalCode || undefined,
          notes: notes || undefined,
        };
      } else if (itemType === 'contact') {
        if (!fullName.trim()) {
          setError('Full name is required');
          return;
        }

        payload = {
          type: 'contact',
          title,
          fullName,
          email: email || undefined,
          phone: phone || undefined,
          address: Object.keys(address).some((k) => address[k as keyof ContactAddress])
            ? address
            : undefined,
          notes: notes || undefined,
        };
      } else {
        setError('Invalid item type');
        return;
      }

      // Generate random item key
      const itemKey = generateRandomKey();

      // Encrypt payload with item key
      const { encryptedPayload, payloadNonce } = encryptPayload(payload, itemKey);

      // Encrypt item key with vault key
      const { itemKeyEncrypted, itemKeyNonce } = encryptItemKey(itemKey, vaultKey);

      await createItem({
        variables: {
          input: {
            workspaceId,
            type: itemType,
            encryptedPayload,
            payloadNonce,
            itemKeyEncrypted,
            itemKeyNonce,
            isFavorite,
          },
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create item');
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    const length = 24;
    let result = '';
    const randomArray = new Uint8Array(length);
    crypto.getRandomValues(randomArray);
    for (let i = 0; i < length; i++) {
      result += chars[randomArray[i] % chars.length];
    }
    setPassword(result);
    setShowPassword(true);
  };

  const renderPasswordForm = () => (
    <>
      <TextField
        fullWidth
        label="Username / Email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        margin="normal"
        autoComplete="off"
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button variant="outlined" size="small" onClick={generatePassword} sx={{ mb: 2 }}>
        Generate Strong Password
      </Button>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Websites
      </Typography>
      {websites.map((url, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            label="URL"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => updateWebsite(index, e.target.value)}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => removeWebsite(index)}
            disabled={websites.length === 1}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addWebsite} sx={{ mb: 2 }}>
        Add Website
      </Button>
    </>
  );

  const renderTOTPForm = () => (
    <>
      <TextField
        fullWidth
        label="Secret Key"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        margin="normal"
        required
        placeholder="Enter or scan QR code"
        helperText="Base32 format (e.g., JBSWY3DPEHPK3PXP)"
      />

      <Button variant="outlined" startIcon={<QrCodeIcon />} sx={{ mb: 2 }} disabled>
        Scan QR Code (Coming Soon)
      </Button>
    </>
  );

  const renderSecureNoteForm = () => (
    <TextField
      fullWidth
      label="Secure Note"
      value={secureNote}
      onChange={(e) => setSecureNote(e.target.value)}
      margin="normal"
      required
      multiline
      rows={8}
      placeholder="Enter your secure note..."
    />
  );

  const renderCreditCardForm = () => (
    <>
      <TextField
        fullWidth
        label="Cardholder Name"
        value={cardholderName}
        onChange={(e) => setCardholderName(e.target.value)}
        margin="normal"
        placeholder="Name as it appears on card"
      />

      <TextField
        fullWidth
        label="Card Number"
        value={cardNumber}
        onChange={(e) => setCardNumber(e.target.value)}
        margin="normal"
        required
        placeholder="1234 5678 9012 3456"
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Expiration Date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            margin="normal"
            placeholder="MM/YY"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="CVV"
            type={showCvv ? 'text' : 'password'}
            value={cvv}
            onChange={(e) => setCvv(e.target.value)}
            margin="normal"
            placeholder="123"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCvv(!showCvv)} size="small">
                    {showCvv ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="PIN"
            type={showPin ? 'text' : 'password'}
            value={cardPin}
            onChange={(e) => setCardPin(e.target.value)}
            margin="normal"
            placeholder="1234"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPin(!showPin)} size="small">
                    {showPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Billing Postal Code"
            value={cardPostalCode}
            onChange={(e) => setCardPostalCode(e.target.value)}
            margin="normal"
          />
        </Grid>
      </Grid>
    </>
  );

  const renderContactForm = () => (
    <>
      <TextField
        fullWidth
        label="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        margin="normal"
      />

      <TextField
        fullWidth
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        margin="normal"
      />

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Address
      </Typography>

      <TextField
        fullWidth
        label="Address Line 1"
        value={address.line1 || ''}
        onChange={(e) => setAddress({ ...address, line1: e.target.value })}
        margin="normal"
      />

      <TextField
        fullWidth
        label="Address Line 2"
        value={address.line2 || ''}
        onChange={(e) => setAddress({ ...address, line2: e.target.value })}
        margin="normal"
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="City"
            value={address.city || ''}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
            margin="normal"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="State / Province"
            value={address.stateOrProvince || ''}
            onChange={(e) => setAddress({ ...address, stateOrProvince: e.target.value })}
            margin="normal"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Postal Code"
            value={address.postalCode || ''}
            onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
            margin="normal"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            select
            label="Country"
            value={address.countryOrRegion || ''}
            onChange={(e) => setAddress({ ...address, countryOrRegion: e.target.value })}
            margin="normal"
          >
            <MenuItem value="">None</MenuItem>
            {COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/vault')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Add Item
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Item Type
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {itemTypeOptions.map((option) => (
              <Grid item xs={6} sm={4} key={option.type}>
                <Button
                  fullWidth
                  variant={itemType === option.type ? 'contained' : 'outlined'}
                  onClick={() => handleTypeChange(option.type)}
                  startIcon={option.icon}
                  sx={{
                    py: 1.5,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  {option.label}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              required
              autoFocus
            />

            {itemType === 'password' && renderPasswordForm()}
            {itemType === 'totp' && renderTOTPForm()}
            {itemType === 'secure_note' && renderSecureNoteForm()}
            {itemType === 'credit_card' && renderCreditCardForm()}
            {itemType === 'contact' && renderContactForm()}

            {itemType !== 'secure_note' && (
              <TextField
                fullWidth
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            )}

            <FormControlLabel
              control={
                <Switch checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} />
              }
              label="Add to favorites"
              sx={{ mt: 2 }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? 'Saving...' : 'Save Item'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
